import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Divider, Group, NumberInput, SegmentedControl, Select, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import dayjs from 'dayjs';
import { defaultWindow } from './kpiCatalog';

// A single reporting-window control shared by every Initiative KPI page. It emits a
// { from, to } window (YYYY-MM-DD strings) — the exact shape the stored NexusQueries expect as
// their {from}/{to} arguments — so changing the period here re-runs every query on the page.
//
// Modes: MTD / QTD / YTD (period-to-date), Trailing N months|quarters|years, and a custom range.
// The default matches defaultWindow() (trailing 3 years), so mounting changes nothing until the
// user interacts.
//
// The window lives in the URL (?from=&to=) via useWindowParams, so a view is shareable by copying
// the address bar and visible to anyone debugging what a user has on screen.

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const fmt = (d) => dayjs(d).format('YYYY-MM-DD');

export function computeWindow(mode, n, unit, range) {
  const today = dayjs();
  const to = fmt(today);
  switch (mode) {
    case 'mtd':
      return { from: fmt(today.startOf('month')), to };
    case 'ytd':
      return { from: fmt(today.startOf('year')), to };
    case 'qtd': {
      // dayjs has no quarter helpers without a plugin, so derive the quarter's first month.
      const qStartMonth = Math.floor(today.month() / 3) * 3;
      return { from: fmt(today.month(qStartMonth).startOf('month')), to };
    }
    case 'trailing': {
      const months = unit === 'years' ? n * 12 : unit === 'quarters' ? n * 3 : n;
      return { from: fmt(today.subtract(months, 'month')), to };
    }
    case 'custom': {
      const [f, t] = range || [];
      if (f && t) return { from: fmt(f), to: fmt(t) };
      return null;
    }
    default:
      return null;
  }
}

// The most recent `count` calendar quarters, newest first, each as an explicit { from, to } range
// (quarter start → quarter end, never past today). Used by the "jump to quarter" dropdown.
function recentQuarters(count) {
  const today = dayjs();
  const out = [];
  let cursor = today;
  for (let i = 0; i < count; i += 1) {
    const qStartMonth = Math.floor(cursor.month() / 3) * 3;
    const start = cursor.month(qStartMonth).startOf('month');
    const end = start.add(2, 'month').endOf('month');
    const to = end.isAfter(today, 'day') ? today : end;
    out.push({
      value: start.format('YYYY-MM-DD'),
      label: `Q${Math.floor(qStartMonth / 3) + 1} ${start.year()}`,
      from: start.format('YYYY-MM-DD'),
      to: fmt(to),
    });
    cursor = start.subtract(1, 'day'); // step back into the previous quarter
  }
  return out;
}

// Best-effort reverse of computeWindow: given a { from, to } window (e.g. from a shared URL),
// work out which control position represents it, so the segmented control reads coherently rather
// than always defaulting to Trailing. Falls back to Custom when the window matches no preset.
function inferState(win) {
  const fallback = { mode: 'trailing', n: 3, unit: 'years', range: [null, null] };
  if (!win?.from || !win?.to) return fallback;
  const from = dayjs(win.from);
  const to = dayjs(win.to);
  const today = dayjs();

  if (to.isSame(today, 'day')) {
    if (from.isSame(today.startOf('month'), 'day')) return { ...fallback, mode: 'mtd' };
    if (from.isSame(today.startOf('year'), 'day')) return { ...fallback, mode: 'ytd' };
    const qStartMonth = Math.floor(today.month() / 3) * 3;
    if (from.isSame(today.month(qStartMonth).startOf('month'), 'day')) return { ...fallback, mode: 'qtd' };

    const months = today.diff(from, 'month');
    if (months >= 1 && from.isSame(today.subtract(months, 'month'), 'day')) {
      if (months % 12 === 0) return { mode: 'trailing', n: months / 12, unit: 'years', range: [null, null] };
      if (months % 3 === 0) return { mode: 'trailing', n: months / 3, unit: 'quarters', range: [null, null] };
      return { mode: 'trailing', n: months, unit: 'months', range: [null, null] };
    }
  }
  return { mode: 'custom', n: 3, unit: 'years', range: [from.toDate(), to.toDate()] };
}

/**
 * Reporting-window state backed by the URL query string (?from=&to=). Returns [window, setWindow]
 * with the same shape the pages used before, so a page opts in by swapping its one useState line.
 * Absent/invalid params fall back to defaultWindow() (trailing 3 years) without dirtying the URL.
 */
export function useWindowParams() {
  const [params, setParams] = useSearchParams();
  const from = params.get('from');
  const to = params.get('to');
  const window = DATE_RE.test(from || '') && DATE_RE.test(to || '') ? { from, to } : defaultWindow();

  const setWindow = (w) => {
    const next = new URLSearchParams(params);
    next.set('from', w.from);
    next.set('to', w.to);
    // replace: keep the address bar current without stacking a history entry per tweak.
    setParams(next, { replace: true });
  };

  return [window, setWindow];
}

export default function DateWindowSelector({ value, onChange }) {
  const [sel, setSel] = useState(() => inferState(value));
  const { mode, n, unit, range } = sel;

  // If the window changes underneath us (a pasted URL, back/forward, an edited address bar) and no
  // longer matches what the control represents, re-infer the control position from it.
  useEffect(() => {
    const computed = computeWindow(sel.mode, sel.n, sel.unit, sel.range);
    if (!computed || computed.from !== value?.from || computed.to !== value?.to) {
      setSel(inferState(value));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.from, value?.to]);

  const emit = (m, num, u, r) => {
    const w = computeWindow(m, num, u, r);
    if (w) onChange(w);
  };

  const handleMode = (m) => {
    setSel((s) => ({ ...s, mode: m }));
    emit(m, n, unit, range); // custom with an incomplete range is a no-op
  };

  const handleN = (val) => {
    const num = Math.max(1, Math.floor(Number(val) || 1));
    setSel((s) => ({ ...s, n: num }));
    if (mode === 'trailing') emit('trailing', num, unit, range);
  };

  const handleUnit = (u) => {
    setSel((s) => ({ ...s, unit: u }));
    if (mode === 'trailing') emit('trailing', n, u, range);
  };

  const handleRange = (r) => {
    setSel((s) => ({ ...s, mode: 'custom', range: r }));
    emit('custom', n, unit, r);
  };

  const quarters = recentQuarters(8);
  const activeQuarter = quarters.find((q) => q.from === value?.from && q.to === value?.to)?.value ?? null;
  const handleQuarter = (v) => {
    const q = quarters.find((x) => x.value === v);
    if (!q) return;
    setSel((s) => ({ ...s, mode: 'custom', range: [dayjs(q.from).toDate(), dayjs(q.to).toDate()] }));
    onChange({ from: q.from, to: q.to });
  };

  return (
    <Card withBorder radius="md" p="sm">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="sm" wrap="wrap">
          <Text size="sm" fw={600}>
            Period
          </Text>
          <SegmentedControl
            size="xs"
            value={mode}
            onChange={handleMode}
            data={[
              { label: 'MTD', value: 'mtd' },
              { label: 'QTD', value: 'qtd' },
              { label: 'YTD', value: 'ytd' },
              { label: 'Trailing', value: 'trailing' },
              { label: 'Custom', value: 'custom' },
            ]}
          />
          {mode === 'trailing' && (
            <Group gap="xs">
              <NumberInput
                size="xs"
                w={72}
                min={1}
                max={20}
                value={n}
                onChange={handleN}
                allowNegative={false}
                allowDecimal={false}
              />
              <SegmentedControl
                size="xs"
                value={unit}
                onChange={handleUnit}
                data={[
                  { label: 'months', value: 'months' },
                  { label: 'quarters', value: 'quarters' },
                  { label: 'years', value: 'years' },
                ]}
              />
            </Group>
          )}
          {mode === 'custom' && (
            <DatePickerInput
              size="xs"
              type="range"
              w={260}
              placeholder="Pick a from – to range"
              valueFormat="YYYY-MM-DD"
              maxDate={new Date()}
              clearable
              value={range}
              onChange={handleRange}
            />
          )}
          <Divider orientation="vertical" />
          <Select
            size="xs"
            w={140}
            placeholder="Jump to quarter"
            data={quarters.map((q) => ({ value: q.value, label: q.label }))}
            value={activeQuarter}
            onChange={handleQuarter}
            comboboxProps={{ withinPortal: true }}
            aria-label="Jump to a specific quarter"
          />
        </Group>
        {value?.from && value?.to && (
          <Text size="xs" c="dimmed">
            Showing {value.from} → {value.to}
          </Text>
        )}
      </Group>
    </Card>
  );
}
